import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useInView, useMotionValue, animate } from "framer-motion";
import {
  Zap, ArrowRight, Play, Check, X, Users, KanbanSquare, Workflow, Layout,
  CalendarDays, MessagesSquare, Megaphone, Star, BarChart3, Building2, Sparkles,
  ShieldCheck, Globe, Mail, Send, MessageSquare, LayoutDashboard, Clock, GitBranch, Tag, Bell,
  Hexagon, Triangle, Aperture, Gem, Flame, Orbit, Command, Boxes,
} from "lucide-react";

const ORB = "https://static.prod-images.emergentagent.com/jobs/0ee1fb53-1779-48ca-b5c0-cc41ae662f87/images/e103baad89e4cf68c0ee03aa3f07e3fd2b19d58c38daf7f80f5d024d313e3cb4.jpeg";
const AURORA = "https://static.prod-images.emergentagent.com/jobs/0ee1fb53-1779-48ca-b5c0-cc41ae662f87/images/a52bb6e667926490fa11e9d5e336d160d1d81a736de0e01cc84be46c49144a34.jpeg";
const RIBBON = "https://static.prod-images.emergentagent.com/jobs/0ee1fb53-1779-48ca-b5c0-cc41ae662f87/images/5a8de0d5aa7938cc56548b196c67fc7ba345e39a48a9b40271770ba565245912.jpeg";

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: 28 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
};

function Counter({ to, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const mv = useMotionValue(0);
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, to, { duration: 1.8, ease: "easeOut", onUpdate: (v) => setVal(v) });
    return () => controls.stop();
  }, [inView, to, mv]);
  const fmt = (n) => n >= 1000000 ? (n / 1000000).toFixed(1) + "M" : n >= 1000 ? Math.round(n / 1000) + "K" : Math.round(n);
  return <span ref={ref}>{prefix}{fmt(val)}{suffix}</span>;
}

const SCREENS = [
  { id: "dashboard", label: "Dashboard", url: "app.momento.io/dashboard", icon: LayoutDashboard, accent: "#6366F1" },
  { id: "pipeline", label: "CRM Pipeline", url: "app.momento.io/pipelines", icon: KanbanSquare, accent: "#06B6D4" },
  { id: "automation", label: "Automations", url: "app.momento.io/automations", icon: Workflow, accent: "#8B5CF6" },
];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const on = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

const screenVariants = {
  enter: { opacity: 0, y: 16 },
  center: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
};

const DashboardScreen = () => (
  <div className="space-y-3" data-testid="live-screen-dashboard">
    <div className="grid grid-cols-3 gap-3">
      {[["Revenue", "$148.5K", "#10B981"], ["Leads", "3,240", "#6366F1"], ["Booked", "412", "#06B6D4"]].map(([l, v, c]) => (
        <div key={l} className="rounded-xl border border-white/8 p-3" style={{ background: "rgba(255,255,255,0.03)" }}>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p>
          <p className="font-display font-bold text-lg mt-1" style={{ color: c }}>{v}</p>
        </div>
      ))}
    </div>
    <div className="rounded-xl border border-white/8 p-3" style={{ background: "rgba(255,255,255,0.03)" }}>
      <div className="flex items-end gap-1.5 h-20">
        {[40, 55, 35, 70, 60, 85, 75, 95, 80, 100].map((h, i) => (
          <motion.div key={i} className="flex-1 rounded-t" style={{ background: "linear-gradient(180deg,#8B5CF6,#6366F1)" }}
            initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: i * 0.045, duration: 0.45 }} />
        ))}
      </div>
    </div>
    <div className="flex items-center gap-2 rounded-xl border border-white/8 p-3" style={{ background: "rgba(255,255,255,0.03)" }}>
      <div className="w-7 h-7 rounded-full bg-indigo-500/25 flex items-center justify-center"><Send className="w-3.5 h-3.5 text-indigo-300" /></div>
      <p className="text-xs text-slate-300 flex-1">"Yes, 2 PM Tuesday works!" <span className="text-slate-500">— auto-booked ✨</span></p>
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">AI</span>
    </div>
  </div>
);

const PipelineScreen = () => {
  const cols = [
    { name: "New Lead", accent: "#6366F1", cards: [["Acme Roofing", "$4,200"], ["Bright Dental", "$1,800"]] },
    { name: "Qualified", accent: "#06B6D4", cards: [["Nova Fitness", "$3,500"]] },
    { name: "Won", accent: "#10B981", cards: [["Peak Legal", "$6,900"], ["Lux Spa", "$2,400"]] },
  ];
  return (
    <div className="grid grid-cols-3 gap-2.5" data-testid="live-screen-pipeline">
      {cols.map((c, ci) => (
        <div key={c.name} className="rounded-xl border border-white/8 p-2.5" style={{ background: "rgba(255,255,255,0.03)" }}>
          <div className="flex items-center gap-1.5 mb-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.accent }} />
            <span className="text-[10px] uppercase tracking-wider text-slate-400 truncate">{c.name}</span>
          </div>
          <div className="space-y-2">
            {c.cards.map(([n, v], i) => (
              <motion.div key={n} className="rounded-lg border border-white/8 p-2" style={{ background: "rgba(255,255,255,0.04)" }}
                initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: ci * 0.1 + i * 0.08, duration: 0.4 }}>
                <p className="text-[11px] text-slate-200 font-medium truncate">{n}</p>
                <p className="text-[10px] mt-0.5 font-semibold" style={{ color: c.accent }}>{v}</p>
              </motion.div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const AutomationScreen = () => {
  const nodes = [
    { icon: Zap, label: "Contact created", accent: "#6366F1", tag: "Trigger" },
    { icon: Mail, label: "Send welcome email", accent: "#EC4899", tag: "Action" },
    { icon: Clock, label: "Wait 1 day", accent: "#F59E0B", tag: "Delay" },
    { icon: GitBranch, label: "If no reply → Telegram", accent: "#8B5CF6", tag: "Condition" },
    { icon: Tag, label: "Tag as 'Nurtured'", accent: "#10B981", tag: "Action" },
  ];
  return (
    <div className="space-y-2" data-testid="live-screen-automation">
      {nodes.map((n, i) => (
        <motion.div key={n.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1, duration: 0.4 }}>
          <div className="flex items-center gap-2.5 rounded-xl border border-white/8 px-3 py-2" style={{ background: "rgba(255,255,255,0.03)" }}>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${n.accent}22` }}>
              <n.icon className="w-3.5 h-3.5" style={{ color: n.accent }} />
            </div>
            <span className="text-xs text-slate-200 flex-1 truncate">{n.label}</span>
            <span className="text-[9px] px-2 py-0.5 rounded-full shrink-0" style={{ background: `${n.accent}1f`, color: n.accent }}>{n.tag}</span>
          </div>
          {i < nodes.length - 1 && <div className="mx-auto w-px h-2 bg-white/12" />}
        </motion.div>
      ))}
    </div>
  );
};

function LiveScreens() {
  const [active, setActive] = useState(0);
  const reduced = usePrefersReducedMotion();
  useEffect(() => {
    if (reduced) return;
    const t = setInterval(() => setActive((a) => (a + 1) % SCREENS.length), 3600);
    return () => clearInterval(t);
  }, [reduced]);
  const s = SCREENS[active];
  return (
    <div className="relative glass rounded-2xl border border-white/10 p-4 sm:p-5" data-testid="live-product-screens"
      style={{ background: "rgba(14,16,23,0.88)", boxShadow: "0 30px 90px -30px rgba(99,102,241,0.45), inset 0 1px 0 rgba(255,255,255,0.06)" }}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2.5 h-2.5 rounded-full bg-rose-400/70" /><div className="w-2.5 h-2.5 rounded-full bg-amber-400/70" /><div className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
        <div className="ml-2 flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 border border-white/8 min-w-0">
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: s.accent }} />
          <span className="text-[11px] text-slate-400 font-mono truncate">{s.url}</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mb-4">
        {SCREENS.map((sc, i) => (
          <button key={sc.id} onClick={() => setActive(i)} data-testid={`live-screen-tab-${sc.id}`}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors"
            style={i === active
              ? { background: `${sc.accent}22`, color: sc.accent, boxShadow: `inset 0 0 0 1px ${sc.accent}55` }
              : { background: "rgba(255,255,255,0.03)", color: "#94a3b8" }}>
            <sc.icon className="w-3.5 h-3.5" />{sc.label}
          </button>
        ))}
      </div>
      <div className="relative min-h-[214px]">
        <AnimatePresence mode="wait">
          <motion.div key={s.id} variants={screenVariants} initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
            {s.id === "dashboard" && <DashboardScreen />}
            {s.id === "pipeline" && <PipelineScreen />}
            {s.id === "automation" && <AutomationScreen />}
          </motion.div>
        </AnimatePresence>
      </div>
      {!reduced && (
        <div className="flex items-center justify-center gap-1.5 mt-4">
          {SCREENS.map((sc, i) => (
            <span key={sc.id} className="h-1 rounded-full transition-all duration-500"
              style={i === active ? { width: 20, background: sc.accent } : { width: 6, background: "rgba(255,255,255,0.15)" }} />
          ))}
        </div>
      )}
    </div>
  );
}

const BRAND_LOGOS = [
  { name: "CRM", icon: Users },
  { name: "Pipelines", icon: KanbanSquare },
  { name: "Automations", icon: Workflow },
  { name: "Calendars", icon: CalendarDays },
  { name: "Unified Inbox", icon: MessagesSquare },
  { name: "Funnels", icon: Layout },
  { name: "Broadcasts", icon: Megaphone },
  { name: "Reviews", icon: Star },
];

const FEATURES = [
  { icon: MessagesSquare, name: "Omnichannel Inbox", desc: "Email, Telegram & SMS in one thread with AI-drafted replies, assignment and internal notes.", accent: "#6366F1", span: "lg:col-span-2 lg:row-span-2", big: true },
  { icon: Workflow, name: "Visual Automations", desc: "Drag-and-drop workflows with conditions, delays and goals.", accent: "#8B5CF6" },
  { icon: KanbanSquare, name: "Pipelines & CRM", desc: "Kanban deal flow with drag-across stages.", accent: "#06B6D4" },
  { icon: Layout, name: "Funnels & Pages", desc: "Blazing-fast public funnels with opt-in capture.", accent: "#10B981" },
  { icon: CalendarDays, name: "Calendars & Booking", desc: "Round-robin scheduling with auto reminders.", accent: "#F59E0B" },
  { icon: Megaphone, name: "Broadcast Campaigns", desc: "Segmented email / Telegram blasts with opens & replies.", accent: "#EC4899" },
  { icon: Star, name: "Reputation & Reviews", desc: "Smart-routed review requests + embeddable widget.", accent: "#F59E0B" },
  { icon: Building2, name: "White-Label Sub-accounts", desc: "Unlimited client tenants, each fully branded.", accent: "#6366F1" },
  { icon: BarChart3, name: "Reporting", desc: "Agency roll-ups & per-client CSV exports.", accent: "#06B6D4" },
];

const COMPARE = [
  ["Modern, zero-lag interface", true, false, "GoHighLevel feels dated and sluggish — Momento is a fast, beautiful SaaS your clients actually enjoy."],
  ["AI woven into every module", true, "limited", "AI is bolted on in GHL. Momento drafts replies, runs the chat widget and grounds answers in your knowledge base."],
  ["Unlimited sub-accounts, no caps", true, false, "GHL's Starter caps you at 3 sub-accounts. Momento gives unlimited clients on Growth and up."],
  ["Free Telegram + unified inbox", true, false, "GHL bills every SMS from a top-up wallet. Momento adds a free Telegram channel in one shared inbox."],
  ["Transparent, all-in pricing", true, false, "No usage wallets or rebill markups — AI drafting and email are included in your plan."],
  ["1-click shareable client reports", true, "limited", "Send clients a live, white-labelled report link plus CSV in a single click."],
];

export default function Landing() {
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);
  const [tools, setTools] = useState(6);
  return (
    <div className="min-h-screen text-slate-100 overflow-x-hidden" style={{ background: "#07080D" }} data-testid="landing-page">
      <style>{`@keyframes marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}`}</style>
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b" style={{ background: "rgba(7,8,13,0.72)", borderColor: "rgba(99,102,241,0.18)" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)", boxShadow: "0 0 20px rgba(99,102,241,0.5)" }}><Zap className="w-4 h-4 text-white" /></div>
            <span className="font-display font-extrabold text-lg tracking-tight">Momento</span>
          </div>
          <nav className="hidden lg:flex items-center gap-7 ml-8 text-sm text-slate-400">
            {["Features", "Why Momento", "Compare", "Pricing", "Reviews"].map((l) => (
              <a key={l} href={`#${l.toLowerCase().replace(/ /g, "-")}`} className="hover:text-white transition-colors" data-testid={`nav-${l.toLowerCase().replace(/ /g, "-")}`}>{l}</a>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <button onClick={() => navigate("/login")} className="px-4 py-2 rounded-full text-sm font-medium text-slate-200 border border-white/15 hover:bg-white/5 transition-colors" data-testid="nav-login-button">Login</button>
            <button onClick={() => navigate("/register")} className="group px-4 sm:px-5 py-2 rounded-full text-sm font-semibold text-white flex items-center gap-1.5 transition-transform hover:scale-[1.03]" style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)", boxShadow: "0 0 24px rgba(99,102,241,0.45)" }} data-testid="nav-start-trial-button">
              <Zap className="w-3.5 h-3.5" />Start free trial
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 sm:pt-40 pb-20 px-5 sm:px-8">
        <div className="absolute inset-0 z-0 opacity-60" style={{ backgroundImage: `url(${AURORA})`, backgroundSize: "cover", backgroundPosition: "center", maskImage: "linear-gradient(to bottom, black 40%, transparent)" }} />
        <div className="absolute inset-0 z-0" style={{ background: "radial-gradient(1200px 500px at 50% -10%, rgba(99,102,241,0.25), transparent 70%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium border" style={{ borderColor: "rgba(139,92,246,0.35)", background: "rgba(139,92,246,0.1)", color: "#c4b5fd" }}>
                <Sparkles className="w-3.5 h-3.5" />AI-Native Agency OS · Replaces 10+ tools
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="font-display font-extrabold tracking-tight leading-[1.05] mt-6 text-4xl sm:text-5xl lg:text-6xl">
                The growth OS built to scale your agency <span style={{ background: "linear-gradient(120deg,#818cf8,#c084fc,#22d3ee)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>10× faster</span>
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="text-slate-400 text-base sm:text-lg mt-6 max-w-xl leading-relaxed">
                Multi-tenant sub-accounts, AI automations, a unified omnichannel inbox, visual pipelines, funnels, booking and white-label portals — everything you need to capture, nurture and close, in one beautiful platform.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="flex flex-wrap gap-3 mt-8">
                <button onClick={() => navigate("/register")} className="group px-6 py-3.5 rounded-full font-semibold text-white flex items-center gap-2 transition-transform hover:scale-[1.03]" style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)", boxShadow: "0 0 32px rgba(99,102,241,0.5)" }} data-testid="hero-start-trial-button">
                  Start 14-day free trial <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
                <a href="#features" className="px-6 py-3.5 rounded-full font-semibold text-slate-200 border border-white/15 flex items-center gap-2 hover:bg-white/5 transition-colors" data-testid="hero-explore-button">
                  <Play className="w-4 h-4" />Explore features
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="flex flex-wrap gap-3 mt-8 text-xs text-slate-400">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass border border-white/10"><Zap className="w-3.5 h-3.5 text-indigo-400" />The all-in-one growth OS for agencies</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass border border-white/10"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />100% white-label & custom domain</span>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="relative">
            <motion.img src={ORB} alt="" className="absolute -top-16 -right-6 w-56 sm:w-72 rounded-full pointer-events-none select-none z-0"
              style={{ filter: "drop-shadow(0 0 60px rgba(99,102,241,0.55))" }}
              animate={{ y: [0, -14, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />
            <div className="relative z-10">
              <LiveScreens />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Client logo wall */}
      <section className="relative px-5 sm:px-8 pt-4 pb-12" data-testid="logo-wall">
        <Reveal className="text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-8">Everything your agency runs on, in one platform</p>
        </Reveal>
        <Reveal delay={0.08} className="max-w-6xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-8 items-center">
          {BRAND_LOGOS.map((b) => (
            <div key={b.name} className="group flex items-center justify-center gap-2.5 text-slate-500 hover:text-slate-200 transition-colors duration-300" data-testid={`brand-${b.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <b.icon className="w-5 h-5 sm:w-6 sm:h-6 shrink-0 opacity-70 group-hover:opacity-100 transition-opacity" strokeWidth={1.75} />
              <span className="font-display font-bold text-base sm:text-lg tracking-tight whitespace-nowrap">{b.name}</span>
            </div>
          ))}
        </Reveal>
      </section>

      {/* Stats band */}
      <section className="relative px-5 sm:px-8 py-14 border-y" style={{ borderColor: "rgba(99,102,241,0.15)", background: "linear-gradient(180deg,rgba(99,102,241,0.06),transparent)" }}>
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {[["Tools in one platform", 9, "", "+"], ["Messaging channels", 3, "", ""], ["Free trial", 14, "", " days"], ["White-label", 100, "", "%"]].map(([label, to, prefix, suffix], i) => (
            <Reveal key={label} delay={i * 0.08}>
              <p className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl" style={{ background: "linear-gradient(120deg,#fff,#a5b4fc)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                <Counter to={to} prefix={prefix} suffix={suffix} />
              </p>
              <p className="text-xs sm:text-sm text-slate-500 mt-2 uppercase tracking-wider">{label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Trust marquee */}
      <div className="py-8 overflow-hidden border-b" style={{ borderColor: "rgba(99,102,241,0.1)" }}>
        <div className="flex gap-10 whitespace-nowrap animate-[marquee_28s_linear_infinite] text-slate-600 text-sm font-medium">
          {[...Array(2)].flatMap((_, k) => ["Missed-call text-back", "AI receptionist", "Unified inbox", "Visual automations", "Online booking", "White-label sub-accounts", "Pipelines & CRM", "Email · SMS · WhatsApp · Telegram"].map((t) => (
            <span key={t + k} className="inline-flex items-center gap-2">{t}<span className="text-indigo-500/40">•</span></span>
          )))}
        </div>
      </div>

      {/* Bento features */}
      <section id="features" className="px-5 sm:px-8 py-24 max-w-7xl mx-auto">
        <Reveal className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">Every tool your agency needs — <span style={{ color: "#a78bfa" }}>beautifully unified</span></h2>
          <p className="text-slate-400 mt-4">One login. One bill. Zero duct-tape integrations.</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[168px]">
          {FEATURES.map((f, i) => (
            <Reveal key={f.name} delay={(i % 4) * 0.06} className={f.span || ""}>
              <div className={`group h-full rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 ${f.big ? "flex flex-col justify-between" : ""}`}
                style={{ borderColor: "rgba(148,163,184,0.14)", background: "rgba(14,16,23,0.7)" }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 0 0 1px ${f.accent}55, 0 20px 60px -20px ${f.accent}55`)}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
                data-testid={`feature-${f.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: `${f.accent}22` }}>
                    <f.icon className="w-5 h-5" style={{ color: f.accent }} />
                  </div>
                  <h3 className="font-display font-semibold text-lg">{f.name}</h3>
                  <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{f.desc}</p>
                </div>
                {f.big && (
                  <div className="mt-5 space-y-2">
                    {[[Mail, "email"], [Send, "telegram"], [MessageSquare, "sms"]].map(([I, ch], k) => (
                      <div key={ch} className="flex items-center gap-2 rounded-lg border border-white/8 px-3 py-2" style={{ background: "rgba(255,255,255,0.03)" }}>
                        <I className="w-3.5 h-3.5 text-indigo-300" />
                        <span className="text-xs text-slate-400 flex-1 capitalize">{ch} thread</span>
                        {k === 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300">AI reply</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Why Momento */}
      <section id="why-momento" className="px-5 sm:px-8 py-24" style={{ background: "linear-gradient(180deg,transparent,rgba(99,102,241,0.05),transparent)" }}>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative">
            <img src={RIBBON} alt="" className="w-full max-w-sm mx-auto rounded-2xl" style={{ filter: "drop-shadow(0 0 50px rgba(139,92,246,0.5))" }} />
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">We remove friction from growth</h2>
            <p className="text-slate-400 mt-4">Built for operators, not just marketers. AI runs across the entire lifecycle so your team executes faster and wins more.</p>
            <div className="grid sm:grid-cols-3 gap-6 mt-8">
              {[["All-in-one", "One platform for the whole client journey", Globe], ["AI-native", "Deep AI across every module, not bolted on", Sparkles], ["Community-driven", "Ship faster with vertical snapshots", Users]].map(([t, d, I]) => (
                <div key={t}>
                  <I className="w-5 h-5 text-indigo-400 mb-2" />
                  <p className="font-semibold text-sm">{t}</p>
                  <p className="text-xs text-slate-400 mt-1">{d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" className="px-5 sm:px-8 py-24 max-w-5xl mx-auto">
        <Reveal className="text-center mb-12 max-w-2xl mx-auto">
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">Momento vs <span style={{ color: "#a78bfa" }}>GoHighLevel</span></h2>
          <p className="text-slate-400 mt-4">Same all-in-one promise — without the dated interface, usage wallets and sub-account caps. Here's exactly how we win.</p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "rgba(148,163,184,0.14)", background: "rgba(14,16,23,0.7)" }}>
            <div className="grid grid-cols-[1fr_84px_120px] sm:grid-cols-[1fr_120px_140px]">
              <div className="px-4 sm:px-6 py-4 text-xs uppercase tracking-wider font-semibold text-slate-500">How we win</div>
              <div className="px-2 py-4 font-display font-bold text-center flex items-center justify-center gap-1.5" style={{ color: "#a78bfa" }}><Zap className="w-4 h-4" />Momento</div>
              <div className="px-2 py-4 font-semibold text-center text-slate-500 text-sm flex items-center justify-center">GoHighLevel</div>
              {COMPARE.map(([label, us, them, reason], i) => (
                <React.Fragment key={i}>
                  <div className="px-4 sm:px-6 py-4 border-t border-white/6">
                    <p className="text-slate-100 font-medium text-sm">{label}</p>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{reason}</p>
                  </div>
                  <div className="px-2 py-4 border-t border-white/6 flex items-start justify-center pt-5">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "rgba(16,185,129,0.18)" }}><Check className="w-4 h-4 text-emerald-400" /></span>
                  </div>
                  <div className="px-2 py-4 border-t border-white/6 flex items-start justify-center pt-5">
                    {them === "limited"
                      ? <span className="text-[10px] font-semibold px-2 py-1 rounded-full" style={{ background: "rgba(245,158,11,0.15)", color: "#fbbf24" }}>Limited</span>
                      : <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "rgba(244,63,94,0.12)" }}><X className="w-4 h-4 text-rose-400/80" /></span>}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
          <p className="text-center text-xs text-slate-600 mt-4">Comparison reflects GoHighLevel's publicly listed plans &amp; usage-based billing as of 2026.</p>
        </Reveal>
      </section>

      {/* Pricing */}
      <section id="pricing" className="px-5 sm:px-8 py-24 max-w-6xl mx-auto">
        <Reveal className="text-center max-w-2xl mx-auto mb-4">
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">Priced to beat the rest</h2>
          <p className="text-slate-400 mt-4">More included, for less. No usage wallet games, no sub-account caps, AI &amp; email built in.</p>
        </Reveal>
        <Reveal delay={0.05} className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium border" style={{ borderColor: "rgba(16,185,129,0.35)", background: "rgba(16,185,129,0.1)", color: "#6ee7b7" }}>
            GoHighLevel charges $97 / $297 / $497 — Momento gives you more for less
          </span>
          <div className="flex items-center justify-center gap-3 mt-6">
            <span className={`text-sm ${!annual ? "text-white font-semibold" : "text-slate-500"}`}>Monthly</span>
            <button onClick={() => setAnnual(!annual)} className="relative w-12 h-6 rounded-full transition-colors" style={{ background: annual ? "#8B5CF6" : "rgba(255,255,255,0.15)" }} data-testid="billing-toggle">
              <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all" style={{ left: annual ? "26px" : "2px" }} />
            </button>
            <span className={`text-sm ${annual ? "text-white font-semibold" : "text-slate-500"}`}>Annual <span className="text-emerald-400">–20%</span></span>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-5 items-stretch">
          {[
            { name: "Starter", price: 77, ghl: 97, tag: "For solo operators", feats: ["Unlimited contacts", "CRM, pipelines & funnels", "Calendars & booking", "Email + free Telegram", "1 sub-account"] },
            { name: "Growth", price: 197, ghl: 297, tag: "Most popular", popular: true, feats: ["Everything in Starter", "Unlimited sub-accounts", "AI automations & inbox", "Broadcasts & reputation", "White-label branding", "Reporting + shareable links"] },
            { name: "Agency Unlimited", price: 397, ghl: 497, tag: "For scaling agencies", feats: ["Everything in Growth", "Custom domains", "Priority support", "Snapshots & rebilling", "Unlimited team seats", "All AI credits included"] },
          ].map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08} className="h-full">
              <div className={`relative h-full rounded-2xl border p-7 flex flex-col ${p.popular ? "" : ""}`}
                style={{ borderColor: p.popular ? "rgba(139,92,246,0.5)" : "rgba(148,163,184,0.14)", background: "rgba(14,16,23,0.75)", boxShadow: p.popular ? "0 0 60px -15px rgba(139,92,246,0.55)" : "none" }}
                data-testid={`pricing-${p.name.toLowerCase().replace(/ /g, "-")}`}>
                {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-semibold text-white" style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)" }}>Most popular</span>}
                <p className="font-display font-semibold text-lg">{p.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{p.tag}</p>
                <div className="mt-5 flex items-end gap-2">
                  <span className="font-display font-extrabold text-4xl">${annual ? Math.round(p.price * 0.8) : p.price}</span>
                  <span className="text-slate-500 text-sm mb-1">/mo{annual ? " billed annually" : ""}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">vs <span className="line-through">${p.ghl}</span> on GoHighLevel</p>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.feats.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#10B981" }} />{f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate("/register")} className="mt-7 w-full py-3 rounded-full font-semibold transition-transform hover:scale-[1.02]"
                  style={p.popular ? { background: "linear-gradient(135deg,#6366F1,#8B5CF6)", color: "#fff", boxShadow: "0 0 24px rgba(99,102,241,0.45)" } : { border: "1px solid rgba(255,255,255,0.15)", color: "#e2e8f0" }}
                  data-testid={`pricing-cta-${p.name.toLowerCase().replace(/ /g, "-")}`}>
                  Start free trial
                </button>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15} className="mt-8">
          <div className="rounded-2xl border p-6 sm:p-8 max-w-3xl mx-auto flex flex-col sm:flex-row items-center gap-6" style={{ borderColor: "rgba(16,185,129,0.3)", background: "rgba(16,185,129,0.05)" }} data-testid="roi-calculator">
            <div className="flex-1 w-full">
              <p className="font-display font-semibold text-lg">ROI calculator</p>
              <p className="text-sm text-slate-400 mt-1">How many separate tools are you paying for today?</p>
              <div className="flex items-center gap-4 mt-4">
                <input type="range" min="2" max="15" value={tools} onChange={(e) => setTools(parseInt(e.target.value))} className="flex-1 accent-emerald-400" data-testid="roi-slider" />
                <span className="font-display font-bold text-xl w-8 text-center">{tools}</span>
              </div>
            </div>
            <div className="text-center sm:text-right shrink-0">
              <p className="text-xs uppercase tracking-wider text-slate-500">Est. monthly savings</p>
              <p className="font-display font-extrabold text-4xl mt-1" style={{ color: "#34d399" }}>${Math.max(0, tools * 65 - (annual ? Math.round(197 * 0.8) : 197)).toLocaleString()}</p>
              <p className="text-xs text-slate-500 mt-1">replacing {tools} tools with Momento Growth</p>
            </div>
          </div>
        </Reveal>
        <Reveal className="text-center mt-8"><p className="text-xs text-slate-500">14-day free trial on every plan · No credit card · Cancel anytime · Save 2 months on annual</p></Reveal>
      </section>

      {/* What you can do */}
      <section id="reviews" className="px-5 sm:px-8 py-24 max-w-6xl mx-auto">
        <Reveal className="text-center mb-14">
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">Everything you need to run an agency</h2>
          <p className="text-slate-400 mt-4 max-w-xl mx-auto">One login to capture leads, book jobs, follow up automatically and keep every client's pipeline moving.</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-5">
          {[["Never miss a lead", "Missed-call text-back and an always-on inbox so every enquiry gets a reply in seconds.", MessagesSquare],
            ["Book more jobs", "Online calendars, reminders and automations that turn interest into booked appointments.", CalendarDays],
            ["Run every client in one place", "White-label sub-accounts, pipelines and reporting for your whole book of business.", Building2]].map(([title, desc, Icon], i) => (
            <Reveal key={title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border p-6" style={{ borderColor: "rgba(148,163,184,0.14)", background: "rgba(14,16,23,0.7)" }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(99,102,241,0.15)" }}><Icon className="w-5 h-5 text-indigo-400" /></div>
                <p className="font-display font-semibold text-lg">{title}</p>
                <p className="text-sm text-slate-400 leading-relaxed mt-2">{desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-5 sm:px-8 pb-24">
        <Reveal className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl border p-10 sm:p-16 text-center overflow-hidden" style={{ borderColor: "rgba(139,92,246,0.4)", background: "rgba(14,16,23,0.8)", boxShadow: "0 0 80px -20px rgba(139,92,246,0.55)" }}>
            <div className="absolute inset-0" style={{ background: "radial-gradient(600px 200px at 50% 0%, rgba(99,102,241,0.25), transparent)" }} />
            <div className="relative z-10">
              <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl">Ready to replace your overpriced stack?</h2>
              <p className="text-slate-400 mt-4 max-w-lg mx-auto">Start building your agency's growth engine with Momento. Explore everything free — no credit card required.</p>
              <button onClick={() => navigate("/register")} className="mt-8 px-8 py-4 rounded-full font-semibold text-white inline-flex items-center gap-2 transition-transform hover:scale-[1.03]" style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)", boxShadow: "0 0 40px rgba(99,102,241,0.55)" }} data-testid="cta-start-trial-button">
                Claim your 14-day free trial <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-xs text-slate-500 mt-4">14-day free trial · Cancel anytime · Money-back guarantee</p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="border-t px-5 sm:px-8 py-14" style={{ borderColor: "rgba(99,102,241,0.12)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)" }}><Zap className="w-4 h-4 text-white" /></div>
              <span className="font-display font-extrabold text-lg">Momento</span>
            </div>
            <span className="inline-flex items-center gap-4 text-xs text-slate-500">
              <a href="mailto:info@momentocrm.com" className="inline-flex items-center gap-1.5 hover:text-slate-200 transition-colors" data-testid="footer-contact-email"><Mail className="w-3.5 h-3.5" />info@momentocrm.com</a>
              <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />All systems operational</span>
            </span>
          </div>
          <p className="font-display font-extrabold tracking-tight text-5xl sm:text-7xl lg:text-8xl mt-10 select-none" style={{ background: "linear-gradient(120deg,rgba(99,102,241,0.25),rgba(139,92,246,0.08))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>MOMENTO</p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-sm text-slate-400">
            <button onClick={() => navigate("/terms")} className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</button>
            <button onClick={() => navigate("/privacy")} className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</button>
            <a href="mailto:info@momentocrm.com" className="hover:text-white transition-colors">Contact</a>
          </div>
          <p className="text-xs text-slate-600 mt-6">© 2026 Momento. The AI-powered growth OS for agencies.</p>
        </div>
      </footer>
    </div>
  );
}
